import React, { useEffect, useState } from "react";
import { CardContent } from "@/components/ui/card";
import "./widgets.css";
import { useDashboardData } from "@/services/dashboardDataService";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import { cleanDateAndTimeObject, renderDate } from "../../../../shared/renderDate";

interface ExternalAuditDateData {
  date: string;
  days: number;
  lastUpdated?: string;
}

const DaysUntilNextExternalAuditDateWidget: React.FC = () => {
  const { data, isLoading, error } = useDashboardData('external-audit-date');
  const [auditDate, setAuditDate] = useState<Date | null>(null);
  const [daysUntil, setDaysUntil] = useState<number>(0);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  // Report widget status to dashboard context
  useWidgetStatusReporter('external-audit-date', isLoading, error, !!data);

  // Calculate days between today and the audit date
  const calculateDaysUntil = (targetDate: Date | null): number => {
    if (!targetDate) return 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    
    const timeDiff = targetDate.getTime() - today.getTime();
    return Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => {
    if (data) {
      const auditData = data as ExternalAuditDateData;
      if (auditData.date) {
        // Try to parse the date directly first (in case it's already a date string)
        let parsedDate = new Date(auditData.date);
        
        // If direct parsing fails, try parsing the parts
        if (isNaN(parsedDate.getTime())) {
          const dateParts = auditData.date.split(/[\/\-.]/);
          
          if (dateParts.length === 3) {
            // Try different formats based on the parts (DD/MM/YYYY)
            parsedDate = new Date(
              parseInt(dateParts[2], 10),
              parseInt(dateParts[1], 10) - 1, // Month is 0-indexed
              parseInt(dateParts[0], 10)
            );
            
            // If the date is still invalid, try a different order (MM/DD/YYYY)
            if (isNaN(parsedDate.getTime())) {
              parsedDate = new Date(
                parseInt(dateParts[2], 10),
                parseInt(dateParts[0], 10) - 1,
                parseInt(dateParts[1], 10)
              );
            }
          }
        }
        
        if (!isNaN(parsedDate.getTime())) {
          parsedDate.setHours(0, 0, 0, 0); // Normalize to start of day
          setAuditDate(parsedDate);
          setDaysUntil(calculateDaysUntil(parsedDate));
        }
      }
      
      if (auditData.lastUpdated) {
        setLastUpdated(cleanDateAndTimeObject(auditData.lastUpdated).toLocaleString());
      }
    }
  }, [data]);

  if (isLoading) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
        <h3 className="text-lg font-semibold mb-2">External Audit Date</h3>
        <div className="text-muted-foreground">Loading...</div>
      </CardContent>
    );
  }

  if (error) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
        <h3 className="text-lg font-semibold mb-2">External Audit Date</h3>
        <div className="text-destructive">Error loading audit date</div>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
      <h3 className="text-lg font-semibold mb-2">External Audit Date</h3>
      <div
        className="flex flex-col items-center justify-center flex-1 w-full h-full"
        style={{ gap: 'clamp(0.5rem, 2cqw, 2rem)' }}
      >
        <span
          className="font-bold text-primary"
          style={{
            fontSize: 'clamp(2.5rem, 8cqw, 7rem)',
            lineHeight: 1,
            minHeight: '1em',
          }}
        >
          {daysUntil}
        </span>
        <span
          className="text-muted-foreground"
          style={{
            fontSize: 'clamp(1rem, 2.5cqw, 2rem)',
            marginBottom: '0.25em',
          }}
        >
          {daysUntil === 1 ? 'day until next audit' : 'days until next audit'}
        </span>
        {auditDate ? (
          <span
            className="text-muted-foreground text-center"
            style={{
              fontSize: 'clamp(0.75rem, 1.5cqw, 1.2rem)',
            }}
          >
            Target: {renderDate(auditDate, { dateStyle: 'medium' })}
          </span>
        ) : null}
        {lastUpdated && (
          <span 
            className="text-xs text-muted-foreground mt-2"
            title={`Last updated: ${lastUpdated}`}
          >
            Updated: {new Date(lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>
    </CardContent>
  );
};

export default React.memo(DaysUntilNextExternalAuditDateWidget);
